import axios from "axios";
import { get } from "lodash-es";
/*
  Important note:
  Any function call in the body of this file has the potential to be executed before the dom body has loaded,
  since the syndication bootstrapper can be added to the head of a file.
*/
import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ABTest } from "../src/packages/ABTest";
import hideFocusOnMouseClick from "../src/packages/hideFocusOnMouseClick";
import { PersonalizationProvider } from "../src/packages/personalization";
import { ConfigProvider } from "./_nextgen/packages/config";
import DefaultQueryProvider from "./api/DefaultQueryProvider";
import Text from "./components/Text";
import { withAnalytics } from "./components/analytics";
import { useBreakpointName } from "./components/breakpoints";
import { createStore } from "./packages/redux";
import BespokeVisualizer from "./syndication/BespokeVisualizer";
import FindItForMe from "./syndication/FindItForMe";
import GlobalFooter from "./syndication/GlobalFooter";
// Transforms
import GlobalNavigation from "./syndication/GlobalNavigation";
import SearchInventoryTool from "./syndication/SearchInventoryTool";

const withBreakpoint = (Component) => {
	return (props) => {
		const breakpoint = useBreakpointName();
		return <Component breakpoint={breakpoint} {...props} />;
	};
};

const API_ROOT = ":items.root.:items";
const ITEM_ROOT = "responsivegrid.:items";

const store = createStore({
	middleware: [],
});

const TRANSFORMS = {
	GlobalNavigation,
	GlobalFooter,
	BespokeVisualizer,
	SearchInventoryTool,
	FindItForMe,
	Default: (data) => data,
};

const SYNDICATED_COMPONENTS = {
	GlobalNavigation: React.lazy(
		() => import("./modules/GlobalNavigation/index.impl"),
	),
	GlobalFooter: React.lazy(() => import("./modules/GlobalFooter")),
	BespokeVisualizer: React.lazy(() => import("./modules/VisualizerBespoke")),
	SearchInventoryTool: React.lazy(
		() => import("./applications/SearchInventoryTool"),
	),
	FindItForMe: React.lazy(() => import("./modules/FindItForMe")),
	DealerOffers: React.lazy(() => import("./modules/DealerOffers")),
	Default: () => <div>Component not found</div>,
};

const fireAnalytics = (eventName, data) => {
	window.dispatchEvent(
		new CustomEvent("LEXUS_ANALYTICS_EVENT", { detail: { eventName, data } }),
	);
};

const stripFirstSlash = (str) => str.replace(/^\//, "");
const ensureLastSlash = (str) => str.replace(/\/?$/, "/");

fireAnalytics("SyndicatedComponent/loaded");

/* global __webpack_public_path__:writable */
window.LEXUS = window.LEXUS || {};

window.LEXUS.baseUrl = window.LEXUS.baseUrl || "https://www.lexus.com";
__webpack_public_path__ = `${ensureLastSlash(
	window.LEXUS.baseUrl,
)}etc.clientlibs/lexus/clientlibs/resources/syndication/`;

window.LEXUS.syndicate = async (components = [], api = "", config = {}) => {
	const syndicationConfig =
		(await axios
			.get(
				`https://www.lexus.com/content/dam/lexus/static/config/syndication.json`,
			)
			.then(({ data }) => data)) || {};
	hideFocusOnMouseClick();

	let rest = {};
	let { analytics: baseAnalytics } = config;
	fireAnalytics("SyndicatedComponent/called");

	if (api) {
		rest = await axios
			.get(
				`${ensureLastSlash(window.LEXUS.baseUrl)}${stripFirstSlash(api)}?pn=y`,
			)
			.then(({ data }) => {
				fireAnalytics("SyndicatedComponent/apiComplete");
				return data;
			});

		const syndicatedConfig = get(rest, `${API_ROOT}.syndicated-config`) || {};

		window.LEXUS.config = window.LEXUS.config
			? {
					...window.LEXUS.config,
					...syndicatedConfig,
					...syndicationConfig,
				}
			: {
					...syndicatedConfig,
					...syndicationConfig,
				};
	}

	components.forEach(
		({
			component,
			key = "",
			data = {},
			transform = true,
			target,
			analytics = {},
			loader = "",
		}) => {
			const SyndicatedComponent = withBreakpoint(
				withAnalytics(
					SYNDICATED_COMPONENTS[component] || SYNDICATED_COMPONENTS["Default"],
				)({
					_component: `SyndicatedComponent`,
					...baseAnalytics,
					...analytics,
				}),
			);
			const transformData = TRANSFORMS[component] || TRANSFORMS["Default"];
			const objKey =
				key === "header" || key === "footer"
					? `${API_ROOT}.${key}.${key}Menu`
					: `${API_ROOT}.${ITEM_ROOT}.${key}`;
			const args = {
				...(key === "header"
					? transformData(
							get(rest, objKey),
							get(rest, `${API_ROOT}.${key}.authConfig`),
						)
					: transformData(get(rest, objKey))),
				...(Object.keys(data).length && transform ? transformData(data) : data),
				syndicated: true,
			};
			const indentifiers = [component, key];
			const componentID = indentifiers.join("_");
			const container = document.querySelector(target);
			//change to createRoot(container!) when switching to TS
			const root = createRoot(container);
			root.render(
				<React.StrictMode>
					<ConfigProvider
						{...process.env}
						{...window?.LEXUS?.config}
						{...syndicationConfig}
						REACT_APP_USE_THIRD_PARTY_INTERSTITIAL={false}
					>
						<PersonalizationProvider>
							<DefaultQueryProvider>
								<Provider store={store}>
									<Suspense fallback={loader ? <Text>{loader}</Text> : <></>}>
										<ABTest name={componentID}>
											<SyndicatedComponent {...args} />
										</ABTest>
									</Suspense>
								</Provider>
							</DefaultQueryProvider>
						</PersonalizationProvider>
					</ConfigProvider>
				</React.StrictMode>,
			);
		},
	);
};
